import {
  Component,
  ComponentInterface,
  ComponentWillLoad,
  Element,
  h,
  Method,
  Prop,
  State,
} from "@stencil/core"

import * as Events from "../../events/events"
import * as Theme from "../../theme/theme"

@Component({
  tag: "sellalong-help-modal",
  scoped: true,
  styleUrls: {
    theme_8629350: "./sellalong-help-modal-theme-sirplus.css",
    theme_27219324: "./sellalong-help-modal.css",
    theme_53156151463: "./sellalong-help-modal.css",
  },
})
export class HelpModal implements ComponentInterface, ComponentWillLoad {
  @Element()
  element!: HTMLElement

  private theme!: Theme.Theme

  @State()
  isVisible: boolean = false

  /**
   * Forces the modal to be visible.
   */
  @Prop()
  overrideVisible: boolean = false

  overlay: HTMLElement | null = null

  async componentWillLoad() {
    this.theme = await Theme.loadTheme(this.element)()

    Events.subscribe("helpModalHideIntent")(this.hide.bind(this))
    Events.subscribe("helpModalShowIntent")(this.show.bind(this))
  }

  disconnectedCallback() {
    Events.publish({ kind: "sellalongComponentRemovedInternal" })
  }

  render() {
    const { text } = this.theme
    const visible = this.overrideVisible || this.isVisible
    const modalClass = {
      "sellalong-help-modal": true,
      "sellalong-help-modal-closed": !visible,
    }
    const overlayClass = {
      "sellalong-help-modal-overlay": true,
      "sellalong-help-modal-closed": !visible,
    }
    return [
      <div class={modalClass}>
        <section>
          <a
            href="#sellalong"
            class="sellalong-help-modal-close-button"
            onClick={(event) => {
              event.preventDefault()
              Events.publish({
                kind: "helpModalHideIntent",
              })
            }}
          >
            <CloseIcon />
          </a>
          <h3 class="sellalong-help-modal-heading">{text("helpModalTitle")}</h3>
          <p>{text("helpModalLeading")}</p>
          <ol class="sellalong-help-modal-steps">
            <li>
              <figure>
                <div class="sellalong-help-modal-icon-wrapper">
                  <MousePointerIcon />
                </div>
                <figcaption>
                  <h4>{text("helpModalStep1Title")}</h4>
                  <p>{text("helpModalStep1Description")}</p>
                </figcaption>
              </figure>
            </li>
            <li>
              <figure>
                <div class="sellalong-help-modal-icon-wrapper">
                  <CardIcon />
                </div>
                <figcaption>
                  <h4>{text("helpModalStep2Title")}</h4>
                  <p>{text("helpModalStep2Description")}</p>
                </figcaption>
              </figure>
            </li>
            <li>
              <figure>
                <div class="sellalong-help-modal-icon-wrapper">
                  <PackageIcon />
                </div>
                <figcaption>
                  <h4>{text("helpModalStep3Title")}</h4>
                  <p>{text("helpModalStep3Description")}</p>
                  <p class="sellalong-help-modal-hint">
                    {text("helpModalStep3Note")}
                  </p>
                </figcaption>
              </figure>
            </li>
          </ol>
          <sellalong-powered-by />
        </section>
      </div>,
      <div
        class={overlayClass}
        onClick={Events.createPublishCallback({ kind: "helpModalHideIntent" })}
      />,
    ]
  }

  @Method()
  async hide() {
    this.isVisible = false

    const panelElement = document.getElementById(
      "sellalong"
    ) as HTMLSellalongPanelElement

    if (panelElement && typeof panelElement["scrollToElement"] === "function") {
      panelElement.scrollToElement()
    }
  }

  @Method()
  async show() {
    this.isVisible = true
  }
}

const MousePointerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="36"
    height="36"
    viewBox="0 0 36 36"
  >
    <g
      fill="none"
      fill-rule="evenodd"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <g stroke="#000" stroke-width="2">
        <g>
          <path
            d="M0 0L14.165 34 19.194 19.194 34 14.165zM20.035 20.035L32.057 32.057"
            transform="translate(-513.000000, -625.000000) translate(514.000000, 626.000000)"
          />
        </g>
      </g>
    </g>
  </svg>
)

const CardIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="46"
    height="34"
    viewBox="0 0 46 34"
  >
    <g
      fill="none"
      fill-rule="evenodd"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <g stroke="#000" stroke-width="2">
        <g transform="translate(-841.000000, -627.000000) translate(842.000000, 628.000000)">
          <rect width="44" height="32" rx="4" />
          <path d="M0 12L44 12" />
        </g>
      </g>
    </g>
  </svg>
)

const PackageIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="38"
    height="42"
    viewBox="0 0 38 42"
  >
    <g
      fill="none"
      fill-rule="evenodd"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <g stroke="#000" stroke-width="2">
        <g>
          <path
            d="M27 14.738L9 4.399M36 27.887V11.95c-.001-1.422-.764-2.735-2-3.446L20 .533c-1.238-.71-2.762-.71-4 0L2 8.504c-1.236.71-1.999 2.024-2 3.446v15.938c.001 1.422.764 2.736 2 3.447l14 7.969c1.238.711 2.762.711 4 0l14-7.97c1.236-.71 1.999-2.024 2-3.446z"
            transform="translate(-1188.000000, -622.000000) translate(1189.000000, 623.000000)"
          />
          <path
            d="M.54 9.877L18 19.938 35.46 9.877M18 40L18 19.918"
            transform="translate(-1188.000000, -622.000000) translate(1189.000000, 623.000000)"
          />
        </g>
      </g>
    </g>
  </svg>
)

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
    <polygon
      fill="currentColor"
      points="427.314 107.313 404.686 84.687 256 233.373 107.314 84.687 84.686 107.313 233.373 256 84.686 404.687 107.314 427.313 256 278.627 404.686 427.313 427.314 404.687 278.627 256 427.314 107.313"
    />
  </svg>
)
